import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import {
  prefetchProductContent,
  getCachedContent,
  getOrFetchProductContent,
  showModalSkeleton,
  SDQuickAddComponent,
} from '@theme/sd-quick-add';

/**
 * SD CTA Quick View - Opens mini PDP modal or adds to cart for single-variant products
 *
 * This component delegates to the existing sd-quick-add-component when available,
 * or triggers the quick add modal directly when the quick add button is disabled.
 */
export class SDCtaQuickView extends Component {
  get isSingleVariant() {
    return this.hasAttribute('data-single-variant');
  }

  get useSmartBehavior() {
    return this.hasAttribute('data-smart-behavior');
  }

  /**
   * Gets the quick add component from the parent product card
   * @returns {HTMLElement & { handleClick?: (e: Event) => void, prefetch?: () => void } | null}
   */
  get quickAddComponent() {
    return this.closest('product-card')?.querySelector('sd-quick-add-component');
  }

  connectedCallback() {
    super.connectedCallback();
    const button = this.querySelector('button');
    button?.addEventListener('pointerenter', this.#handlePrefetch, { once: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const button = this.querySelector('button');
    button?.removeEventListener('pointerenter', this.#handlePrefetch);
  }

  /**
   * Prefetches the product page on hover via the shared cache utility.
   */
  #handlePrefetch = () => {
    const url = this.#getProductPageUrl();
    if (!url) return;
    prefetchProductContent(url);
  };

  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Smart behavior: single variant adds to cart directly
    if (this.useSmartBehavior && this.isSingleVariant) {
      await this.#addToCart();
      return;
    }

    // Try to use existing quick add component (reuses its caching and logic)
    const quickAdd = this.quickAddComponent;
    if (quickAdd?.handleClick) {
      // Set product title for mobile modal header
      quickAdd.dataset.productTitle = this.dataset.productTitle || '';
      quickAdd.handleClick(event);
      return;
    }

    // Fallback: open modal directly (when quick add button is disabled)
    await this.#openModalDirectly();
  };

  async #addToCart() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const button = this.querySelector('button');
    button?.classList.add('loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (response.ok) {
        const cart = await response.json();
        document.dispatchEvent(
          new CartAddEvent(cart, 'sd-cta-quick-view', {
            source: 'sd-cta-quick-view',
            variantId: variantId,
          })
        );
        button?.classList.add('atc-added');
        setTimeout(() => button?.classList.remove('atc-added'), 2000);
      } else {
        button?.classList.add('atc-error');
        setTimeout(() => button?.classList.remove('atc-error'), 2000);
      }
    } finally {
      button?.classList.remove('loading');
    }
  }

  async #openModalDirectly() {
    const url = this.#getProductPageUrl();

    // Temp component for updateQuickAddModal (needs product card context for mobile restructuring)
    const tempComponent = /** @type {InstanceType<typeof SDQuickAddComponent>} */ (
      document.createElement('sd-quick-add-component')
    );
    tempComponent.dataset.productTitle = this.dataset.productTitle || '';
    this.appendChild(tempComponent);

    try {
      let productGrid = getCachedContent(url);

      if (productGrid) {
        await tempComponent.updateQuickAddModal(/** @type {Element} */ (productGrid.cloneNode(true)));
        /** @type {HTMLElement & { showDialog?: () => void } | null} */
        const dialog = document.getElementById('sd-quick-add-dialog');
        dialog?.showDialog?.();
        return;
      }

      // Cache miss — show skeleton and open modal immediately
      showModalSkeleton(true);
      /** @type {HTMLElement & { showDialog?: () => void } | null} */
      const dialog = document.getElementById('sd-quick-add-dialog');
      dialog?.showDialog?.();

      productGrid = await getOrFetchProductContent(url);
      if (productGrid) {
        await tempComponent.updateQuickAddModal(/** @type {Element} */ (productGrid.cloneNode(true)));
      }

      showModalSkeleton(false);
    } finally {
      tempComponent.remove();
    }
  }

  #getProductPageUrl() {
    const productCard = this.closest('product-card');
    const productLink = productCard?.querySelector('a[href*="/products/"]');

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);
    url.searchParams.set('view', 'sd-mini-pdp-DO-NOT-DELETE');
    return url.toString();
  }
}

if (!customElements.get('sd-cta-quick-view')) {
  customElements.define('sd-cta-quick-view', SDCtaQuickView);
}
